import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { AppMode, AuthService, MasqueradeState, User } from '../../core/auth/auth.service';
import { ApiService, OrganizationMembershipOption, UserCreditsResponse } from '../../shared/services/api.service';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppSettingsService, AppSettings } from '../../shared/services/app-settings.service';
import { ThemeService } from '../../shared/services/theme.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css']
})
export class SidebarComponent implements OnInit, OnDestroy {
  private static readonly SIDEBAR_CACHE_PREFIX = 'tbds.sidebar.cache.';
  private static readonly MOBILE_BREAKPOINT_PX = 960;
  user$: Observable<User | null>;
  masqueradeState$: Observable<MasqueradeState | null>;
  isAdmin = false;
  isPlatformAdmin = false;
  appMode: AppMode = 'org';
  canToggleMode = false;
  isMobileView = false;
  isMobileMenuOpen = false;
  userCredits: UserCreditsResponse | null = null;
  orgOptions: OrganizationMembershipOption[] = [];
  selectedOrgId: string | null = null;
  switchingOrg = false;
  settings: AppSettings | null = null;
  isBrandingLoading = true;
  isDarkMode$ = this.themeService.isDarkMode$;
  private currentUserId: string | null = null;
  private userInfoSummarySubscription?: Subscription;
  private summaryRefreshTriggerSubscription?: Subscription;
  private routerSubscription?: Subscription;
  private isSummaryRefreshInFlight = false;
  private shouldRefreshAfterCurrent = false;
  private readonly onWindowFocus = this.handleWindowFocus.bind(this);
  private readonly onVisibilityChange = this.handleVisibilityChange.bind(this);
  private readonly onWindowResize = this.handleWindowResize.bind(this);

  constructor(
    private authService: AuthService,
    private router: Router,
    private apiService: ApiService,
    private appSettingsService: AppSettingsService,
    private themeService: ThemeService,
    private snackBar: MatSnackBar
  ) {
    this.user$ = this.authService.getCurrentUser();
    this.masqueradeState$ = this.authService.getMasqueradeState();
  }

  ngOnInit(): void {
    this.updateResponsiveLayout();

    // Subscribe to user$ and update isAdmin immediately
    this.user$.subscribe(user => {
      this.currentUserId = user?.uid || null;
      this.isAdmin = user?.role === 'admin';
      this.isPlatformAdmin = user?.isPlatformAdmin === true;
      this.appMode = this.authService.resolveAppMode(user);
      
      // Load user credits for all users (admins can see their own credits too)
      if (user) {
        this.restoreCachedSidebarValues(user.uid);
        this.isBrandingLoading = !this.appSettingsService.hasCachedBranding(this.apiService.getActiveOrgId());
        this.loadOrganizationOptions();
        if (!this.isPlatformContext) {
          this.refreshUserInfoSummary();
        }
      } else {
        this.userCredits = null;
        this.orgOptions = [];
        this.selectedOrgId = null;
        this.canToggleMode = false;
        this.isBrandingLoading = false;
      }
    });

    // Refresh summary when navigating to a new route.
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.currentUserId && !this.isPlatformContext) {
          this.refreshUserInfoSummary();
        }
      });

    // Refresh summary after successful mutating operations.
    this.summaryRefreshTriggerSubscription = this.apiService.userInfoSummaryRefreshRequested$.subscribe(() => {
      if (this.currentUserId && !this.isPlatformContext) {
        this.refreshUserInfoSummary();
      }
    });

    // Refresh summary when window regains focus (catches updates made on the same page)
    window.addEventListener('focus', this.onWindowFocus);
    window.addEventListener('resize', this.onWindowResize);

    // Refresh summary when tab becomes visible after being backgrounded.
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // Subscribe to app branding settings (cache + API refresh)
    this.appSettingsService.settings$.subscribe({
      next: (settings) => {
        this.settings = settings;
      },
      error: (error) => {
        console.error('Failed to load app settings for sidebar branding:', error);
        this.settings = null;
      }
    });
  }

  private handleWindowFocus(): void {
    // Refresh summary when window regains focus.
    if (this.currentUserId && !this.isPlatformContext) {
      this.refreshUserInfoSummary();
    }
  }

  ngOnDestroy(): void {
    if (this.userInfoSummarySubscription) {
      this.userInfoSummarySubscription.unsubscribe();
    }
    if (this.summaryRefreshTriggerSubscription) {
      this.summaryRefreshTriggerSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    window.removeEventListener('focus', this.onWindowFocus);
    window.removeEventListener('resize', this.onWindowResize);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  private handleWindowResize(): void {
    this.updateResponsiveLayout();
  }

  private updateResponsiveLayout(): void {
    this.isMobileView = window.innerWidth < SidebarComponent.MOBILE_BREAKPOINT_PX;
    if (!this.isMobileView) {
      this.isMobileMenuOpen = false;
    }
  }

  private refreshUserInfoSummary(): void {
    if (!this.currentUserId) return;

    if (this.isSummaryRefreshInFlight) {
      this.shouldRefreshAfterCurrent = true;
      return;
    }

    if (this.userInfoSummarySubscription) {
      this.userInfoSummarySubscription.unsubscribe();
    }

    this.isSummaryRefreshInFlight = true;
    this.userInfoSummarySubscription = this.apiService.getUserInfoSummary().subscribe({
      next: (summary) => {
        this.userCredits = {
          creditsRemaining: summary.credits.creditsRemaining,
          includedRemaining: summary.credits.includedRemaining,
          prepaidRemaining: summary.credits.prepaidRemaining,
          individualRemaining: summary.credits.individualRemaining,
          teamRemaining: summary.credits.teamRemaining,
          expiringSoonCredits: summary.credits.expiringSoonCredits,
          nextIndividualExpiryAt: summary.credits.nextIndividualExpiryAt,
          nextIndividualExpiryCredits: summary.credits.nextIndividualExpiryCredits,
          nextIndividualExpiryDays: summary.credits.nextIndividualExpiryDays,
          planDisplayName: this.formatSidebarPlanLabel(
            summary.billing?.planCode,
            summary.billing?.planDisplayName,
            summary.billing?.accountScope
          ),
          billingTermLabel: this.formatBillingTermLabel(
            summary.billing?.currentPeriodStartAt,
            summary.billing?.currentPeriodEndAt,
            summary.billing?.accountScope,
            summary.billing?.planCode
          ),
        };
        this.saveCachedSidebarValues();
      },
      error: (error: any) => {
        console.error('Failed to load user info summary:', error);
        this.isSummaryRefreshInFlight = false;
        if (this.shouldRefreshAfterCurrent) {
          this.shouldRefreshAfterCurrent = false;
          this.refreshUserInfoSummary();
        }
        // Keep last known value to avoid sidebar flicker.
      },
      complete: () => {
        this.isSummaryRefreshInFlight = false;
        if (this.shouldRefreshAfterCurrent) {
          this.shouldRefreshAfterCurrent = false;
          this.refreshUserInfoSummary();
        }
      }
    });
  }

  private handleVisibilityChange(): void {
    if (!document.hidden && this.currentUserId && !this.isPlatformContext) {
      this.refreshUserInfoSummary();
    }
  }

  private restoreCachedSidebarValues(userId: string): void {
    try {
      // Drop legacy user-only cache keys that mixed credits across orgs.
      localStorage.removeItem(`${SidebarComponent.SIDEBAR_CACHE_PREFIX}${userId}`);
      const orgId = this.apiService.getActiveOrgId() || 'none';
      const raw = localStorage.getItem(`${SidebarComponent.SIDEBAR_CACHE_PREFIX}${userId}:${orgId}`);
      if (!raw) {
        // Do not reuse another org's cached credits for this user.
        this.userCredits = null;
        return;
      }
      const parsed = JSON.parse(raw) as {
        userCredits?: UserCreditsResponse | null;
        orgOptions?: OrganizationMembershipOption[];
        selectedOrgId?: string | null;
      };

      if (parsed.userCredits && typeof parsed.userCredits.creditsRemaining === 'number') {
        this.userCredits = parsed.userCredits;
      }
      if (Array.isArray(parsed.orgOptions)) {
        this.orgOptions = parsed.orgOptions;
        this.canToggleMode = this.isPlatformAdmin && this.orgOptions.length > 0;
      }
      if (typeof parsed.selectedOrgId === 'string' && parsed.selectedOrgId.trim()) {
        this.selectedOrgId = parsed.selectedOrgId.trim();
      }
    } catch {
      // Ignore malformed cache
    }
  }

  private saveCachedSidebarValues(): void {
    if (!this.currentUserId) return;
    try {
      const orgId = this.apiService.getActiveOrgId() || this.selectedOrgId || 'none';
      const payload = {
        userCredits: this.userCredits,
        orgOptions: this.orgOptions,
        selectedOrgId: this.selectedOrgId,
      };
      localStorage.setItem(
        `${SidebarComponent.SIDEBAR_CACHE_PREFIX}${this.currentUserId}:${orgId}`,
        JSON.stringify(payload)
      );
    } catch {
      // Ignore cache write errors
    }
  }

  get creditsBreakdownTooltip(): string {
    const planCredits = Number(this.userCredits?.includedRemaining || 0);
    const topupCredits = Number(this.userCredits?.prepaidRemaining || 0);
    const days = this.userCredits?.nextIndividualExpiryDays;
    const remaining = Number(this.userCredits?.creditsRemaining || 0);
    if (remaining <= 0) {
      return 'No credits yet — click to open Account Info and top up.';
    }
    const lines = [`${planCredits} plan credits and ${topupCredits} top-up credits.`];

    if (days != null && topupCredits > 0) {
      lines.push(`Top-up credits expire in ${days} days.`);
    }

    return lines.join(' ');
  }

  onCreditsClick(event: Event): void {
    if (!this.userCredits || this.userCredits.creditsRemaining > 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.navigateTo('/my-account');
  }

  private formatSidebarPlanLabel(
    planCode?: string | null,
    planDisplayName?: string | null,
    accountScope?: 'individual' | 'team' | null
  ): string | null {
    const code = String(planCode || '').trim().toUpperCase();
    if (code === 'INDIVIDUAL_PAYG' || code.includes('PAYG')) {
      return 'Pay as you go';
    }
    if (code === 'INDIVIDUAL_TRIAL' || code.includes('TRIAL')) {
      return 'Trial Plan';
    }
    if (code.startsWith('TEAM') || accountScope === 'team') {
      return 'Team Plan';
    }
    if (code === 'INDIVIDUAL_STANDARD' || code.startsWith('INDIVIDUAL')) {
      return 'Individual Plan';
    }
    if (accountScope === 'individual') {
      return 'Individual Plan';
    }
    const raw = String(planDisplayName || '').trim().toLowerCase();
    if (!raw) {
      return null;
    }
    if (raw.includes('pay as you go') || raw.includes('payg')) {
      return 'Pay as you go';
    }
    if (raw.includes('trial')) {
      return 'Trial Plan';
    }
    if (raw.includes('team')) {
      return 'Team Plan';
    }
    if (raw.includes('individual')) {
      return 'Individual Plan';
    }
    return planDisplayName || null;
  }

  private formatBillingTermLabel(
    _periodStartAt?: string | null,
    periodEndAt?: string | null,
    _accountScope?: 'individual' | 'team' | null,
    planCode?: string | null
  ): string | null {
    const code = String(planCode || '').trim().toUpperCase();
    if (code === 'INDIVIDUAL_PAYG' || code.includes('PAYG')) {
      return null;
    }
    if (!periodEndAt) {
      return null;
    }
    const endMs = Date.parse(periodEndAt);
    if (!Number.isFinite(endMs)) {
      return null;
    }
    // Inclusive period end → exclusive next boundary (renewal instant in SAST).
    const renewParts = this.getBillingCalendarParts(endMs + 1);
    if (!renewParts) {
      return null;
    }
    return `Renews ${renewParts.day} ${renewParts.monthShort}`;
  }

  private getBillingCalendarParts(epochMs: number): {
    day: number;
    monthIndex: number;
    monthShort: string;
    year: number;
  } | null {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Africa/Johannesburg',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).formatToParts(new Date(epochMs));
      const day = Number(parts.find((part) => part.type === 'day')?.value);
      const monthShortRaw = String(parts.find((part) => part.type === 'month')?.value || '').trim();
      const monthShort = monthShortRaw.replace(/\.$/, '');
      const year = Number(parts.find((part) => part.type === 'year')?.value);
      if (!Number.isFinite(day) || !monthShort || !Number.isFinite(year)) {
        return null;
      }
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIndex = monthNames.findIndex(
        (name) => name.toLowerCase() === monthShort.slice(0, 3).toLowerCase()
      );
      return {
        day,
        monthIndex: monthIndex >= 0 ? monthIndex : 0,
        monthShort,
        year,
      };
    } catch {
      return null;
    }
  }

  async logout(): Promise<void> {
    this.isMobileMenuOpen = false;
    await this.authService.logout();
  }

  stopMasquerade(): void {
    this.authService.stopMasquerade();
    this.apiService.requestUserInfoSummaryRefresh();
    void this.router.navigate([this.isPlatformContext ? '/platform-admin' : '/admin/users']);
  }

  navigateTo(route: string): void {
    if (this.isMobileView) {
      this.isMobileMenuOpen = false;
    }
    this.router.navigate([route]);
  }

  get showOrgSelector(): boolean {
    return !this.isPlatformContext && this.orgOptions.length >= 2;
  }

  get isPlatformContext(): boolean {
    return this.isPlatformAdmin && this.appMode === 'platform';
  }

  onModeChanged(mode: AppMode): void {
    if (!this.isPlatformAdmin || !this.canToggleMode || mode === this.appMode) {
      return;
    }
    this.appMode = mode;
    this.authService.setPreferredAppMode(mode);
    this.snackBar.open(`Switched to ${mode === 'platform' ? 'Platform Admin' : 'Organization'}`, 'Close', {
      duration: 2200,
    });
    if (mode === 'platform') {
      this.navigateTo('/platform-admin');
      return;
    }
    if (this.isAdmin) {
      this.navigateTo('/admin/new-request');
      return;
    }
    this.navigateTo('/user/data-request');
  }

  onOrganizationChanged(orgId: string): void {
    const nextOrgId = String(orgId || '').trim();
    if (!nextOrgId || nextOrgId === this.selectedOrgId || this.switchingOrg) {
      return;
    }
    this.switchingOrg = true;
    this.apiService.selectMyOrganization(nextOrgId).subscribe({
      next: () => {
        this.selectedOrgId = nextOrgId;
        this.reloadPage();
      },
      error: () => {
        this.switchingOrg = false;
      },
    });
  }

  protected reloadPage(): void {
    window.location.reload();
  }

  toggleMobileMenu(): void {
    if (!this.isMobileView) {
      return;
    }
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  toggleTheme(): void {
    this.themeService.toggleDarkMode();
  }

  private loadOrganizationOptions(): void {
    this.apiService.listMyOrganizations().subscribe({
      next: (response) => {
        this.orgOptions = response.organizations || [];
        this.canToggleMode = this.isPlatformAdmin && this.orgOptions.length > 0;
        if (this.isPlatformAdmin && !this.canToggleMode) {
          this.appMode = 'platform';
          this.authService.setPreferredAppMode('platform');
        }
        this.selectedOrgId = response.activeOrgId || this.apiService.getActiveOrgId();
        const selectedOrg = this.orgOptions.find((org) => org.orgId === this.selectedOrgId) || this.orgOptions[0];
        if (selectedOrg) {
          this.appSettingsService.applyOrganizationBranding({
            orgId: selectedOrg.orgId,
            displayName: selectedOrg.displayName,
            logoUrl: selectedOrg.logoUrl || null,
            primaryColor: selectedOrg.primaryColor || null,
            secondaryColor: selectedOrg.secondaryColor || null,
          });
        }
        this.saveCachedSidebarValues();
        this.isBrandingLoading = false;
      },
      error: () => {
        // Keep cached/previous org state on transient failures to avoid mode toggle flicker.
        this.isBrandingLoading = false;
      },
    });
  }
}

